import { useEffect, useRef, useState, useMemo } from "react";
import { TouchableOpacity, View } from "react-native";
import { Text } from "@shared/components/text";
import { CustomCheckbox } from "@shared/components/md-design-system/Checkbox/CustomCheckbox";
import VoiceTextArea from "@shared/components/form/VoiceTextArea";
import {
  QuestionnaireAnswer,
  QuestionnaireQuestionOption,
  ensureOtherOptionSelected as buildEnsureOtherOptionSelected,
  getAnswerSelectedTexts,
  getAnswerSelectedValues,
  isOtherOptionVisuallySelected,
  toggleMultipleChoiceOption,
  updateOtherTextAnswer,
} from "app/_modules/core/questionnaire";

const OTHER_TEXTAREA_STYLE = { height: 96 };

interface MultipleChoiceOptionsProps {
  orderedOptions: QuestionnaireQuestionOption[];
  answer?: QuestionnaireAnswer;
  onAnswerChange: (answer: QuestionnaireAnswer) => void;
  voiceLocale: string;
  voiceMessages: { permissionDenied: string; notAvailable: string; error: string };
}

export const MultipleChoiceOptions = ({
  orderedOptions,
  answer,
  onAnswerChange,
  voiceLocale,
  voiceMessages,
}: MultipleChoiceOptionsProps) => {
  const [isOtherDictating, setIsOtherDictating] = useState(false);
  const answerRef = useRef<QuestionnaireAnswer | undefined>(answer);
  const selectedValuesRef = useRef<string[]>([]);
  const selectedTextsRef = useRef<string[]>([]);

  useEffect(() => {
    answerRef.current = answer;
  }, [answer]);

  const selectedValues = useMemo(() => getAnswerSelectedValues(answer), [answer]);
  const selectedTexts = useMemo(() => getAnswerSelectedTexts(answer), [answer]);

  useEffect(() => {
    selectedValuesRef.current = selectedValues;
    selectedTextsRef.current = selectedTexts;
  }, [selectedValues, selectedTexts]);

  const getAnswerContext = () => ({
    orderedOptions,
    selectedValues: selectedValuesRef.current,
    selectedTexts: selectedTextsRef.current,
    currentOtherText: answerRef.current?.otherText || "",
  });

  const commitAnswerChange = (nextAnswer: QuestionnaireAnswer) => {
    answerRef.current = nextAnswer;
    selectedValuesRef.current = getAnswerSelectedValues(nextAnswer);
    selectedTextsRef.current = getAnswerSelectedTexts(nextAnswer);
    onAnswerChange(nextAnswer);
  };

  const ensureOtherOptionSelected = () => {
    const nextAnswer = buildEnsureOtherOptionSelected(getAnswerContext());
    if (!nextAnswer) return;
    commitAnswerChange(nextAnswer);
  };

  const handleToggleOption = (option: QuestionnaireQuestionOption) => {
    const nextAnswer = toggleMultipleChoiceOption(option, getAnswerContext(), isOtherDictating);
    if (!nextAnswer) return;
    commitAnswerChange(nextAnswer);
  };

  const handleUpdateOtherText = (value: string) => {
    commitAnswerChange(updateOtherTextAnswer(value, getAnswerContext()));
  };

  const shouldShowOtherTextInput = useMemo(() => {
    const otherOption = orderedOptions.find((option) => option.is_other);
    if (!otherOption) return false;
    return selectedValues.includes(otherOption.value) || isOtherDictating;
  }, [isOtherDictating, orderedOptions, selectedValues]);

  return (
    <View className="flex-col gap-2">
      {orderedOptions.map((option) => {
        const translatedOption = { ...option };
        return (
          <TouchableOpacity
            key={option.id || option.value}
            className={`flex-row items-center justify-start gap-2 p-4 border rounded-lg ${
              isOtherOptionVisuallySelected(option, selectedValues, isOtherDictating)
                ? 'border-secondary-600 bg-secondary-100'
                : 'border-gray-300 bg-white'
            }`}
            onPress={() => handleToggleOption(translatedOption)}
            activeOpacity={0.7}
          >
            <CustomCheckbox
              checked={isOtherOptionVisuallySelected(option, selectedValues, isOtherDictating)}
              onChange={() => handleToggleOption(translatedOption)}
            />
            <Text>{option.label}</Text>
          </TouchableOpacity>
        );
      })}
      {shouldShowOtherTextInput && (
        <VoiceTextArea
          rows={3}
          value={answer?.otherText || undefined}
          onChangeText={handleUpdateOtherText}
          textAreaStyle={OTHER_TEXTAREA_STYLE}
          voiceToTextEnabled
          voiceLocale={voiceLocale}
          voiceMessages={voiceMessages}
          onBeforeVoiceStart={ensureOtherOptionSelected}
          onVoiceStart={() => setIsOtherDictating(true)}
          onVoiceEnd={() => {
            ensureOtherOptionSelected();
            setIsOtherDictating(false);
          }}
          onVoiceError={() => {
            ensureOtherOptionSelected();
            setIsOtherDictating(false);
          }}
        />
      )}
    </View>
  );
};
